// FILE: components/layout/Footer.tsx
"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { Mail, Facebook, Youtube, Globe } from "lucide-react";

// Type khớp với getSiteSettings() — chỉ lấy field Footer cần
interface FooterSettings {
  site_name?: string | null;
  site_description?: string | null;
  facebook_url?: string | null;
  youtube_url?: string | null;
  contact_email?: string | null;
}

interface FooterProps {
  /** Server-fetched settings từ getSiteSettings(). Nếu không pass, Footer dùng giá trị fallback. */
  settings?: FooterSettings;
  /** Variant: 'app' = compact (dưới AppShell), 'marketing' = full (landing/legal pages) */
  variant?: "app" | "marketing";
}

const linkGroups = {
  product: {
    title: "Sản phẩm",
    links: [
      { label: "Khám phá", href: "/explore" },
      { label: "Lộ trình học", href: "/paths" },
      { label: "Đề thi", href: "/exams" },
      { label: "Review", href: "/reviews" },
      { label: "Bảng xếp hạng", href: "/leaderboard" },
    ],
  },
  support: {
    title: "Hỗ trợ",
    links: [
      { label: "Trợ giúp", href: "/help" },
      { label: "Hướng dẫn", href: "/help#guide" },
      { label: "Báo lỗi", href: "/help#feedback" },
    ],
  },
  legal: {
    title: "Pháp lý",
    links: [
      { label: "Điều khoản dịch vụ", href: "/terms" },
      { label: "Chính sách bảo mật", href: "/privacy" },
      { label: "Chính sách cookie", href: "/cookies" },
    ],
  },
};

export default function Footer({ settings, variant = "app" }: FooterProps) {
  const siteName = settings?.site_name ?? "JB AI";
  const tagline =
    settings?.site_description ??
    "Nền tảng học thi thông minh, đồng hành cùng bạn chinh phục các kỳ thi ngân hàng.";

  // Compact variant — chỉ hiện copyright + legal links 1 dòng (dùng trong AppShell)
  if (variant === "app") {
    return (
      <footer className="border-t border-border bg-white">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-3 px-4 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
            <span>©</span>
            <span className="font-display font-extrabold text-teal-800">JB</span>
            <span className="font-display font-extrabold text-orange-500">AI</span>
            <span>· 2026 · Job & Banking</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[12px] text-muted-foreground">
            <Link
              href="/terms"
              className="hover:text-teal-700 hover:underline transition-colors"
            >
              Điều khoản
            </Link>
            <span className="text-border">·</span>
            <Link
              href="/privacy"
              className="hover:text-teal-700 hover:underline transition-colors"
            >
              Bảo mật
            </Link>
            <span className="text-border">·</span>
            <Link
              href="/help"
              className="hover:text-teal-700 hover:underline transition-colors"
            >
              Trợ giúp
            </Link>
            <span className="text-border">·</span>
            <a
              href={`mailto:${settings?.contact_email ?? "lienhejb@gmail.com"}`}
              className="hover:text-teal-700 hover:underline transition-colors"
            >
              Liên hệ
            </a>
          </div>
        </div>
      </footer>
    );
  }

  // Marketing variant — full footer 4 cột (dùng cho /terms, /privacy, landing nếu có)
  return (
    <footer className="border-t border-border bg-white">
      <div className="mx-auto max-w-7xl px-6 py-12 lg:px-8 lg:py-14">
        {/* Top section */}
        <div className="grid grid-cols-2 gap-8 sm:grid-cols-4 lg:grid-cols-5">
          {/* Brand column */}
          <div className="col-span-2">
            <Link href="/" className="inline-flex items-center gap-1">
              <span className="font-display text-2xl font-extrabold text-teal-800">
                JB
              </span>
              <span className="font-display text-2xl font-extrabold text-orange-500">
                AI
              </span>
            </Link>
            <p className="mt-3 max-w-xs text-[13px] leading-relaxed text-muted-foreground">
              {tagline}
            </p>

            {/* Social links */}
            <div className="mt-5 flex items-center gap-2">
              {settings?.facebook_url && (
                <a
                  href={settings.facebook_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-border text-muted-foreground transition-all hover:border-teal-200 hover:bg-teal-50 hover:text-teal-700"
                >
                  <Facebook className="h-4 w-4" />
                </a>
              )}
              {settings?.youtube_url && (
                <a
                  href={settings.youtube_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="YouTube"
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-border text-muted-foreground transition-all hover:border-teal-200 hover:bg-teal-50 hover:text-teal-700"
                >
                  <Youtube className="h-4 w-4" />
                </a>
              )}
              {settings?.contact_email && (
                <a
                  href={`mailto:${settings.contact_email}`}
                  aria-label="Email"
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-border text-muted-foreground transition-all hover:border-teal-200 hover:bg-teal-50 hover:text-teal-700"
                >
                  <Mail className="h-4 w-4" />
                </a>
              )}
              <a
                href="https://web.jb.com.vn"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Website"
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-border text-muted-foreground transition-all hover:border-teal-200 hover:bg-teal-50 hover:text-teal-700"
              >
                <Globe className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Link columns */}
          {Object.entries(linkGroups).map(([key, group]) => (
            <div key={key}>
              <h3 className="font-display text-[11px] font-extrabold uppercase tracking-wider text-foreground">
                {group.title}
              </h3>
              <ul className="mt-4 space-y-2.5">
                {group.links.map((link) => (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      className="text-[13px] text-muted-foreground transition-colors hover:text-teal-700"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom section */}
        <div className="mt-10 flex flex-col items-start gap-3 border-t border-border pt-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-[12px] text-muted-foreground">
            © 2026 <span className="font-semibold text-foreground">{siteName}</span> ·
            Job & Banking. Tất cả quyền được bảo lưu.
          </p>
          <p className="text-[12px] text-muted-foreground">
            Made with <span className="text-orange-500">♥</span> in Vietnam
          </p>
        </div>
      </div>
    </footer>
  );
}
